import { Pressable } from 'react-native';
import { Text } from 'tamagui';
import { useConfirm, useThemeColors, Icon } from '@kiips/ui';

interface Props {
  /** Visible label and accessibility name (e.g. "Remove from day"). */
  label: string;
  /** Title for the confirmation dialog. */
  confirmTitle: string;
  /** Body message shown in the confirmation dialog. */
  confirmMessage: string;
  /** Called when the user confirms. Persistence and navigation belong here. */
  onConfirm: () => void;
}

/**
 * Canonical destructive footer button shared by every detail / edit
 * screen (place, stay, journey, trip-edit panel, day-edit panel). One
 * visual treatment, one confirmation flow — callers only supply copy
 * and the action.
 *
 * Confirmation flows through `useConfirm` (ADR-0075 / Phase 5):
 * native iOS / Android fall through to the system `Alert.alert` for
 * idiomatic chrome; web renders the custom `ConfirmDialog` panel with
 * focus-trap + Esc-dismiss + the warning haptic on confirm. Both
 * paths converge on the same boolean resolution.
 */
export function DeleteRowButton({ label, confirmTitle, confirmMessage, onConfirm }: Props) {
  const theme = useThemeColors();
  const confirm = useConfirm();
  const handlePress = async () => {
    const ok = await confirm({
      title: confirmTitle,
      message: confirmMessage,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) onConfirm();
  };
  return (
    <Pressable
      onPress={handlePress}
      accessibilityLabel={label}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingVertical: 14,
          paddingHorizontal: 16,
          borderRadius: 14,
          backgroundColor: theme.surface,
        },
        pressed && { opacity: 0.7 },
      ]}
    >
      <Icon name="delete-outline" size={18} color={theme.destructive} />
      <Text fontSize={14} fontWeight="600" color="$destructive">{label}</Text>
    </Pressable>
  );
}
